import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, BellRing } from 'lucide-react';

import { API_BASE_URL } from '@/lib/apiBase';
import { useWebPush } from '@/hooks/useWebPush';

const API = API_BASE_URL;

const ProfilePage = () => {
  const { user, setUser, setToken } = useAuth();
  const [saving, setSaving] = useState(false);
  const webpush = useWebPush();

  const handleToggleWebPush = async () => {
    if (webpush.subscribed) {
      const res = await webpush.unsubscribe();
      if (res.ok) toast.success('Push notifications disabled');
      else toast.error(res.error || 'Failed to disable');
    } else {
      const res = await webpush.subscribe();
      if (res.ok) toast.success('Push notifications enabled');
      else toast.error(res.error || 'Failed to enable');
    }
  };

  const handleTestWebPush = async () => {
    const res = await webpush.sendTest();
    if (res.ok) toast.success('Test notification sent');
    else toast.error(res.error || 'Test failed');
  };

  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    email_signature: user?.email_signature || '',
  });

  const [pinForm, setPinForm] = useState({
    current_pin: '',
    new_pin: '',
  });
  const [pinSaving, setPinSaving] = useState(false);

  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState(user?.email || '');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/me`, profile);
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        setToken(res.data.token);
      }
      if (res.data?.user) {
        setUser(res.data.user);
      }
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePin = async (e) => {
    e.preventDefault();
    setPinSaving(true);
    try {
      await axios.post(`${API}/auth/pin`, {
        current_pin: pinForm.current_pin,
        new_pin: pinForm.new_pin,
      });
      toast.success('PIN updated');
      setPinForm({ current_pin: '', new_pin: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PIN');
    } finally {
      setPinSaving(false);
    }
  };

  const requestRecoveryCode = async () => {
    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/superadmin-recovery/request`, { email: recoveryEmail });
      toast.success('If eligible, a code has been sent');
    } catch (err) {
      toast.error('Failed to request code');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const confirmRecoveryCode = async () => {
    setRecoveryLoading(true);
    try {
      const res = await axios.post(`${API}/auth/superadmin-recovery/confirm`, { email: recoveryEmail, code: recoveryCode });
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        setToken(res.data.token);
      }
      if (res.data?.user) {
        setUser(res.data.user);
      }
      toast.success('Super Admin restored');
      setRecoveryOpen(false);
      setRecoveryCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          My Profile
        </h1>
        <p className="text-slate-600">Update your account details and login PIN.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Update your name and email. If you change your email, you’ll stay logged in.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                data-testid="profile-name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                data-testid="profile-email"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-signature">Email signature</Label>
              <textarea
                id="profile-signature"
                value={profile.email_signature}
                onChange={(e) => setProfile({ ...profile, email_signature: e.target.value })}
                placeholder={"e.g.\nThanks,\nJane · Support · CRM Sync\nhttps://yourapp.com"}
                rows={4}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                data-testid="profile-signature"
              />
              <p className="text-xs text-slate-500">
                Appended automatically at the bottom of every ticket reply you send (above any quoted thread). Plain text; line breaks preserved.
                <br/>
                <span className="text-slate-400">Variables: </span>
                <code className="text-slate-600">{`{{ticket_number}} {{customer_name}} {{customer_email}} {{location_name}} {{module}} {{user_name}}`}</code>
              </p>
            </div>

            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="profile-save">
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change PIN</CardTitle>
          <CardDescription>
            Set a permanent 4-digit PIN to login. If you already have a PIN, enter your current PIN to change it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSavePin} className="space-y-4">
            <div className="space-y-2">
              <Label>Current PIN (only if already set)</Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="••••"
                value={pinForm.current_pin}
                onChange={(e) => {
                  const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                  setPinForm({ ...pinForm, current_pin: v });
                }}
                data-testid="pin-current"
              />
            </div>
            <div className="space-y-2">
              <Label>New PIN</Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="••••"
                value={pinForm.new_pin}
                onChange={(e) => {
                  const v = (e.target.value || '').replace(/\D/g, '').slice(0, 4);
                  setPinForm({ ...pinForm, new_pin: v });
                }}
                data-testid="pin-new"
                required
              />
            </div>

            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={pinSaving} data-testid="pin-save">
              {pinSaving ? 'Saving...' : 'Update PIN'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Push notifications */}
      <Card data-testid="webpush-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="w-5 h-5 text-indigo-600" />
            Browser Notifications
          </CardTitle>
          <CardDescription>
            Get a push notification when a ticket is created, replied to, or resolved — even when
            the tab is in the background. Enable this on each device you want to receive alerts on.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!webpush.supported ? (
            <div className="text-sm text-slate-600">
              Your browser doesn't support Web Push. Try Chrome, Edge, Firefox, or install the PWA from the home screen.
            </div>
          ) : webpush.permission === 'denied' ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
              Notifications are <strong>blocked</strong> in your browser. Click the 🔒 icon in the address bar
              and allow notifications for this site, then refresh.
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                onClick={handleToggleWebPush}
                disabled={webpush.loading}
                className={webpush.subscribed ? 'bg-slate-200 text-slate-900 hover:bg-slate-300' : 'bg-indigo-600 hover:bg-indigo-700'}
                data-testid="webpush-toggle"
              >
                {webpush.loading ? (
                  'Please wait…'
                ) : webpush.subscribed ? (
                  <>
                    <BellOff className="w-4 h-4 mr-1.5" />
                    Disable on this device
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-1.5" />
                    Enable push on this device
                  </>
                )}
              </Button>
              {webpush.subscribed && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestWebPush}
                  data-testid="webpush-test"
                >
                  Send test
                </Button>
              )}
              <span className={`text-xs ${webpush.subscribed ? 'text-emerald-700' : 'text-slate-500'}`}>
                {webpush.subscribed ? '🔔 Subscribed on this device' : 'Not enabled on this device'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Super Admin recovery (safe allowlist) */}
      <Card>
        <CardHeader>
          <CardTitle>Super Admin Recovery</CardTitle>
          <CardDescription>
            If you accidentally lost Super Admin permissions, you can restore it with an email code (only available for approved email).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => { setRecoveryEmail(user?.email || ''); setRecoveryOpen(true); }}>
            Restore Super Admin
          </Button>
        </CardContent>
      </Card>

      <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Super Admin</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Code</Label>
              <Input value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} placeholder="6-digit code" />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={requestRecoveryCode} disabled={recoveryLoading}>
                {recoveryLoading ? 'Sending...' : 'Send Code'}
              </Button>
              <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={confirmRecoveryCode} disabled={recoveryLoading || !recoveryCode}>
                {recoveryLoading ? 'Verifying...' : 'Confirm'}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setRecoveryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ProfilePage;
